import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MultiTable from "../../../Common/MUITable";
import { BalancePointActionModal } from "./VehicleBalancePointsModals";
import DelhiReportExport from "./DelhiReportExport";
import { GetVehicleHaltPointBalanceDetails } from "./ReportApi";

const NAVY = "#003366";

const VehicleBalancePointsInnerReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const parentRow = location.state?.parentRow;
  const parentRows = location.state?.parentRows || [];
  const showAllPoints = Boolean(location.state?.showAllPoints);
  const coverageParent = location.state?.coverageParent;

  const [actionPoint, setActionPoint] = useState(null);
  const [showAction, setShowAction] = useState(false);
  const [haltPoints, setHaltPoints] = useState([]);
  const [loading, setLoading] = useState(false);

  const circleId =
    parentRow?.circleId ||
    parentRow?.wardId ||
    coverageParent?.circleId ||
    coverageParent?.wardId ||
    undefined;
  const vehicleId = showAllPoints ? undefined : parentRow?.vehicleId || undefined;

  const fetchBalancePoints = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];

      if (showAllPoints) {
        const res = await GetVehicleHaltPointBalanceDetails({ circleId });
        if (res?.data) {
          data = Array.isArray(res.data) ? res.data : res.data.data || [];
        }
      } else {
        const res = await GetVehicleHaltPointBalanceDetails({ circleId, vehicleId });
        if (res?.data) {
          data = Array.isArray(res.data) ? res.data : res.data.data || [];
        }
      }

      setHaltPoints(data.map((point) => ({ ...point, status: point.status || "Pending" })));
    } catch (error) {
      console.error("Error fetching balance halt points:", error);
      setHaltPoints([]);
    } finally {
      setLoading(false);
    }
  }, [circleId, vehicleId, showAllPoints]);

  useEffect(() => {
    fetchBalancePoints();
  }, [fetchBalancePoints]);

  const handleBack = () => {
    navigate("/vehicle-coverage-report", {
      state: coverageParent ? { parentRow: coverageParent } : undefined,
    });
  };

  const exportColumns = useMemo(() => {
    const base = [
      { label: "Halt Point", field: "haltPoint" },
      { label: "Latitude", field: "latitude" },
      { label: "Longitude", field: "longitude" },
      { label: "Location", field: "location" },
      { label: "Status", field: "status" },
    ];
    if (showAllPoints) {
      return [{ label: "Vehicle Number", field: "vehicleNo" }, ...base];
    }
    return base;
  }, [showAllPoints]);

  const tableColumns = useMemo(() => {
    const cols = [];
    if (showAllPoints) {
      cols.push({
        label: "Vehicle Number",
        field: "vehicleNo",
        width: "140px",
      });
    }
    cols.push(
      {
        label: "Halt Point",
        field: "haltPoint",
        width: "140px",
      },
      {
        label: "Latitude",
        field: "latitude",
        width: "130px",
      },
      {
        label: "Longitude",
        field: "longitude",
        width: "130px",
      },
      {
        label: "Location",
        field: "location",
        width: "180px",
      },
      {
        label: "Status",
        field: "status",
        width: "120px",
      },
      {
        label: "Action",
        field: "action",
        width: "100px",
        render: (row) => (
          <button
            type="button"
            className="btn btn-link p-0"
            title="Action"
            onClick={() => {
              setActionPoint(row);
              setShowAction(true);
            }}
            style={{ color: NAVY, fontSize: "18px" }}
          >
            <i className="fa-solid fa-eye" />
          </button>
        ),
      }
    );
    return cols;
  }, [showAllPoints]);

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">
              Balance Points
              {showAllPoints
                ? " - All"
                : parentRow?.vehicleNo || parentRow?.vehicleNumber
                  ? ` - ${parentRow.vehicleNo || parentRow.vehicleNumber}`
                  : ""}
            </h5>
            <p className="text-muted mb-0">
              View balance halt points
              {showAllPoints
                ? " for all vehicles."
                : " for the selected vehicle."}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="button-container"
              onClick={handleBack}
            >
              Back
            </button>
            <DelhiReportExport
              reportData={haltPoints}
              reportTitle="Balance Points"
              columns={exportColumns}
              fileName="BalancePointsReport"
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="fw-semibold mb-3">
              Vehicle Number :{" "}
              {showAllPoints ? "All" : parentRow?.vehicleNo || parentRow?.vehicleNumber || "—"}
            </div>

            <div className="table-responsive">
              <MultiTable
                needSRNumber
                columns={tableColumns}
                rows={haltPoints}
                tableHeight={420}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <BalancePointActionModal
        show={showAction}
        point={actionPoint}
        onClose={() => {
          setShowAction(false);
          setActionPoint(null);
        }}
        onSuccess={fetchBalancePoints}
      />
    </div>
  );
};

export default VehicleBalancePointsInnerReport;
